import React, { createContext, useState, useMemo, useEffect } from 'react';
// IMPORT: MUI Theme Engine
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

export const ThemeContext = createContext(null);

export const useTheme = () => React.useContext(ThemeContext);

const hexToRgb = (hex) => {
    let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}` : null;
};

export const getShapeClasses = (shape) => {
    switch (shape) {
        case 'pill': return 'rounded-full';
        case 'square': return 'rounded-none';
        case 'rounded': default: return 'rounded-md';
    }
};

export const DEFAULT_THEME = {
    mode: 'light',
    accentColor: '#3b82f6',
    shape: 'rounded',
    font: 'Inter',
    sidebarTheme: { bg: 'bg-gray-800', text: 'text-gray-200' },
    headerTheme: { bg: 'bg-white' }
};

const ALL_FONTS = ['Inter', 'Poppins', 'Lora', 'Roboto Slab', 'Inconsolata', 'JetBrains Mono'];

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(DEFAULT_THEME);

    useEffect(() => {
        // This effect loads all available fonts from Google Fonts
        const fontUrl = `https://fonts.googleapis.com/css2?family=${ALL_FONTS.map(f => f.replace(' ', '+')).join('&family=')}:wght@400;700&display=swap`;
        const linkId = 'google-font-stylesheet';
        let link = document.getElementById(linkId);
        if (!link) {
            link = document.createElement('link');
            link.id = linkId;
            link.rel = 'stylesheet';
            document.head.appendChild(link);
        }
        link.href = fontUrl;
    }, []);

    useEffect(() => {
        const root = window.document.documentElement;
        const rgbColor = hexToRgb(theme.accentColor);

        if (theme.mode === 'dark') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }

        const dynamicStyles = `
            :root {
                --primary-rgb: ${rgbColor};
                --font-family: '${theme.font}', sans-serif;
            }
            body {
                font-family: var(--font-family);
                /* Ensure body background matches the theme immediately */
                background-color: ${theme.mode === 'dark' ? '#0f172a' : '#f8fafc'}; 
                color: ${theme.mode === 'dark' ? '#e2e8f0' : '#1e293b'};
            }
        `;

        const styleElementId = 'dynamic-theme-styles';
        let styleElement = document.getElementById(styleElementId);
        if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = styleElementId;
            document.head.appendChild(styleElement);
        }
        styleElement.innerHTML = dynamicStyles;

    }, [theme.mode, theme.accentColor, theme.font]);

    // --- UNIVERSAL TEXT SIZE REDUCER ---
    // This configures the global MUI theme to be compact by default.
    const muiTheme = useMemo(() => createTheme({
        palette: {
            mode: theme.mode,
            primary: { main: theme.accentColor },
            ...(theme.mode === 'dark' ? {
                background: {
                    default: '#0f172a', // Slate 900 (Matches body)
                    paper: '#1e293b',   // Slate 800 (Matches dark:bg-gray-800ish)
                },
                text: {
                    primary: '#f1f5f9', // Slate 100
                    secondary: '#94a3b8', // Slate 400
                },
            } : {
                // Light mode defaults
                background: {
                    default: '#f8fafc',
                    paper: '#ffffff',
                },
                text: {
                    primary: '#1e293b',
                    secondary: '#64748b',
                },
            }),
        },
        typography: {
            fontFamily: theme.font,
            fontSize: 12, // Global base size reduction (Standard is 14)
        },
        components: {
            // Force 'small' size on all inputs/buttons globally
            MuiButton: { defaultProps: { size: 'small' } },
            MuiIconButton: { defaultProps: { size: 'small' } },
            MuiTextField: { defaultProps: { size: 'small' } },
            MuiSelect: { defaultProps: { size: 'small' } },
            MuiFormControl: { defaultProps: { size: 'small' } },
            MuiChip: { defaultProps: { size: 'small' } },

            // Granular font reductions
            MuiInputBase: {
                styleOverrides: {
                    root: { fontSize: '0.85rem' }
                }
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: { fontSize: '0.85rem', minHeight: 32 }
                }
            },
            MuiTypography: {
                styleOverrides: {
                    // Scale down default body text
                    body1: { fontSize: '0.85rem' },
                    body2: { fontSize: '0.75rem' }
                }
            }
        }
    }), [theme.mode, theme.accentColor, theme.font]);

    const themeValue = useMemo(() => ({ theme, setTheme }), [theme]);

    return (
        <ThemeContext.Provider value={themeValue}>
            {/* Wrap app in MUI Theme Provider to apply the reducer */}
            <MuiThemeProvider theme={muiTheme}>
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
};